import type { StringTable } from './ru';

export const en: StringTable = {
  'app.title': 'Vision & Lens Simulator',
  'lang.ru': 'Русский',
  'lang.uz': 'Oʻzbekcha',
  'lang.en': 'English',

  'app.subtitle': 'Left: how you see now. Right: how you would see after the chosen treatment.',
  'step.now': '1. How you see now',
  'step.after': '2. Treatment',
  'step.scene': 'Where we look',
  'badge.now': 'Now, without treatment',
  'badge.after': 'After treatment',
  'age': 'Age',
  'target': 'Lens set for',
  'target.far': 'distance',
  'target.near': 'near',
  'gaze': 'Gaze follows the pointer',
  'zoom': 'Enlarge the picture',
  'tags': 'Distance tags',
  'how': 'How it works',
  'webgl.error': 'The simulation needs a WebGL2 browser: Chrome, Edge, Firefox or Safari 16+.',
  'rendering': 'Rendering…',
  'stage': 'Stage',
  'tag.far': '5 m',
  'tag.mid': '66 cm',
  'tag.near': '40 cm',
  'tag.m4': '4 m',
  'tag.m30': '30 m',
  'tag.m60': '60 m',
  'tag.cm75': '75 cm',
  'tag.cm45': '45 cm',
  'zoom.hint': 'Scroll the picture',

  'treat.title': 'Treatment',
  'treat.none': 'No treatment',
  'treat.glasses': 'Glasses or soft lenses',
  'treat.glasses_aphakic': 'Aphakic glasses',
  'treat.progressive': 'Progressive glasses',
  'treat.progressive_aphakic': 'Aphakic bifocals',
  'treat.rgp': 'Rigid contact lenses',
  'treat.rgp_aphakic': 'Aphakic contact lenses',
  'treat.lasik': 'Laser correction',
  'treat.icl': 'Phakic lens (ICL)',
  'treat.iol': 'Lens replacement',
  'lens.title': 'Artificial lens (IOL)',
  'lens.iol_mono': 'Monofocal',
  'lens.iol_monoplus': 'Enhanced monofocal',
  'lens.iol_edof': 'EDOF',
  'lens.iol_tri': 'Trifocal',
  'version.title': 'Lens version',
  'version.plain': 'Standard',
  'version.toric': 'Toric — corrects astigmatism',

  'refr.title': 'Refraction',
  'refr.hint': 'From your glasses prescription or an autorefractometer printout. A plus cylinder is accepted too.',
  'refr.se': 'Spherical equivalent {x}',
  'refr.meridians': 'meridians {m1} and {m2}',
  'refr.kind.emmetropia': 'emmetropia',
  'refr.kind.myopia': 'myopia',
  'refr.kind.hyperopia': 'hyperopia',
  'astig.simple_myopic': 'simple myopic astigmatism',
  'astig.compound_myopic': 'compound myopic astigmatism',
  'astig.simple_hyperopic': 'simple hyperopic astigmatism',
  'astig.compound_hyperopic': 'compound hyperopic astigmatism',
  'astig.mixed': 'mixed astigmatism',

  'cond.title': 'Eye conditions',
  'cond.aphakia': 'Aphakia (no lens)',
  'cond.aphakia.info': 'An eye without its lens: no focusing of its own; SPH is usually about +10…+12 D.',
  'cond.cataract': 'Cataract',
  'cond.cataract.stages': ['early', 'immature', 'mature', 'hypermature'],
  'cond.kc': 'Keratoconus',
  'cond.kc.stages': ['stage I', 'stage II', 'stage III', 'stage IV'],
  'cond.glaucoma': 'Glaucoma',
  'cond.glaucoma.stages': ['early', 'moderate', 'advanced', 'end-stage'],
  'cond.amd': 'AMD (age-related macular degeneration)',
  'cond.amd.stages': ['early', 'intermediate', 'late'],
  'cond.dr': 'Diabetic retinopathy',
  'cond.dr.stages': ['non-proliferative', 'pre-proliferative', 'proliferative'],
  'cond.hr': 'Hypertensive retinopathy',
  'cond.hr.stages': ['angiopathy', 'angiosclerosis', 'retinopathy', 'neuroretinopathy'],
  'cond.rp': 'Retinitis pigmentosa',
  'cond.rp.stages': ['early', 'moderate', 'advanced', 'end-stage'],
  'cond.amblyopia': 'Amblyopia (lazy eye)',
  'cond.amblyopia.stages': ['very mild', 'mild', 'moderate', 'severe', 'very severe'],
  'cond.amblyopia.visus': ['VA 0.8–0.9', 'VA 0.4–0.7', 'VA 0.2–0.3', 'VA 0.05–0.1', 'VA ≤ 0.04'],
  'cond.amblyopia.hint': 'VA is the best corrected acuity for this degree; without correction it is lower.',

  'corr.none.label': 'No correction',
  'corr.none.desc': 'How the eye sees now.',
  'corr.glasses.label': 'Glasses / soft lenses (distance)',
  'corr.glasses.desc': 'Correct myopia, hyperopia and astigmatism. After ~45, near vision stays blurred without reading glasses.',
  'corr.glasses_aphakic.label': 'Aphakic glasses',
  'corr.glasses_aphakic.desc': 'Thick plus lenses (about +10…+12 D): distance is sharp, reading needs extra power. Objects look about a quarter larger, the field narrows and the edges distort.',
  'corr.progressive.label': 'Progressive glasses',
  'corr.progressive.desc': 'Top for distance, middle for screens, bottom for reading.',
  'corr.progressive_aphakic.label': 'Aphakic bifocal / progressive glasses',
  'corr.progressive_aphakic.desc': 'Top for distance, bottom for reading with a full add (about +3 D): without the lens there is no focusing of its own. Magnification as with aphakic glasses.',
  'corr.rgp.label': 'Rigid / scleral lenses',
  'corr.rgp.desc': 'Mask an irregular cornea in keratoconus; usually give the sharpest vision.',
  'corr.rgp_aphakic.label': 'Aphakic contact lenses',
  'corr.rgp_aphakic.desc': 'The lens sits on the cornea: less magnification (about 8 %) and a wider field than aphakic glasses.',
  'corr.lasik.label': 'Laser correction (LASIK / SMILE)',
  'corr.lasik.desc': 'Reshapes the cornea. Night halos are possible. Does not fix age-related near blur.',
  'corr.icl.label': 'Phakic lens (ICL)',
  'corr.icl.desc': 'Implanted in front of your own lens; focusing ability stays. Often used for high myopia.',
  'corr.iol_mono.label': 'Monofocal IOL',
  'corr.iol_mono.desc': 'Sharp at one distance (usually far). Reading glasses needed. Minimal halos.',
  'corr.iol_monoplus.label': 'Enhanced monofocal IOL',
  'corr.iol_monoplus.desc': 'Slightly extends intermediate vision; halos like a monofocal.',
  'corr.iol_edof.label': 'EDOF IOL (extended depth of focus)',
  'corr.iol_edof.desc': 'Distance and intermediate without glasses; small print may still need glasses. Small halos.',
  'corr.iol_tri.label': 'Trifocal IOL (multifocal)',
  'corr.iol_tri.desc': 'Distance, intermediate and reading without glasses. Light is split between foci: lower contrast, halos around headlights at night.',
  'corr.icl_toric.label': 'Toric phakic lens (ICL)',
  'corr.iol_mono_toric.label': 'Toric monofocal IOL',
  'corr.iol_monoplus_toric.label': 'Toric enhanced monofocal IOL',
  'corr.iol_edof_toric.label': 'Toric EDOF IOL',
  'corr.iol_tri_toric.label': 'Toric trifocal IOL',
  'corr.toric.desc': 'The toric version also corrects astigmatism.',

  'w.kc_lasik': 'Laser correction is usually contraindicated in keratoconus.',
  'w.high_lasik': 'Beyond −8…−10 D laser is usually not used; ICL is often considered.',
  'w.lasik_near': 'After laser correction of myopia, near vision without glasses worsens with age: your own myopia no longer helps you read, and presbyopia remains.',
  'w.aphakia_lasik': 'Laser correction is usually not used in aphakia: the hyperopia is too high. An IOL or contact lenses are considered.',
  'w.cat_icl': 'ICL is not used with cataract; the lens is usually replaced.',
  'w.cat_glasses': 'Glasses and laser do not remove a cloudy lens.',
  'w.retina_multi': 'Multifocal and EDOF lenses reduce contrast; they are usually not advised with AMD, glaucoma, retinopathy or retinitis pigmentosa. Discuss with your doctor.',
  'w.astig_nontoric': 'Astigmatism will remain; toric versions exist.',
  'w.retina_any': 'Lens surgery and glasses do not restore the retina or optic nerve.',
  'w.clear_lens_young': 'Clear-lens replacement before 40 is rare: natural focusing is lost.',
  'w.amblyopia': 'With amblyopia (lazy eye) correction raises acuity only up to the limit set by the degree of amblyopia: the visual system did not develop in childhood. Treatment is possible at an early age. Discuss with your doctor.',

  'n.aphakia': 'Aphakia is an eye without its lens. Uncorrected, it is strongly long-sighted and cannot focus near. Aphakic glasses make objects about a quarter larger and narrow the field; contact lenses magnify less; an IOL is often implanted later.',
  'n.rp_dark': 'With retinitis pigmentosa it is hard to see when moving from a bright room into a dark one: the eyes adapt to the dark much more slowly and less than healthy eyes. The «Bright to dark» button under the pictures shows it next to a healthy eye.',
  'n.hr': 'Hypertensive retinopathy is a change of the retinal vessels with high blood pressure. In the early stages vision usually does not change and only the doctor sees it in the fundus; later come spots, oedema and loss of vision. Blood-pressure control matters.',
  'n.dysphotopsia': 'Dysphotopsias — light effects — are possible: halos (glowing rings around lights), glare and starbursts. They are stronger at night and with a wide pupil and usually fade over time. The «Night lights» scene shows them close up.',

  'demo.dark': '▶ Bright to dark: dark adaptation',
  'demo.stop': '■ Stop the demo',
  'demo.caption': 'From a bright room into the dark · {x} min',
  'demo.left': 'Without retinitis pigmentosa',
  'demo.right': 'With retinitis pigmentosa',

  'disclaimer': 'For information only. Not a medical device and not intended for diagnosis or choosing treatment. Real vision depends on the eye, the brain, lighting and adaptation. Decide together with your doctor.',

  'how.items': [
    'Every object in the scene sits at its own distance: far, 66 cm or 40 cm. For each distance the model traces rays through the pupil with the aberrations of the eye and the chosen lens, and blurs the image separately for red, green and blue light.',
    'Age sets the focusing reserve (presbyopia) and the pupil size; the scene lighting changes the pupil too.',
    'Multifocal and EDOF lenses split light between foci: the unfocused part of the light makes halos around bright lights. Scatter in the eye makes glare, irregularities of the lens and cornea make starbursts. A screen is never as bright as headlights, so night scenes are shown with compressed brightness to keep halos and glare visible. The «Night lights» scene shows them close up.',
    'Cataract: light scatter (haze, glare), yellowing and irregular aberrations. Glaucoma, AMD, diabetic and hypertensive retinopathy and retinitis pigmentosa affect the retina and the nerve; they are shown as blur and fading, not black patches, because that is how patients describe them. Amblyopia (lazy eye): the optics are fine but the brain never learned to see sharply; shown as a blur that correction does not remove. Aphakia is an eye without its lens: strong long-sight and no near focus.',
    'This is a simplification. You look at the screen with your own eyes, the brain takes weeks to adapt to new lenses, and lenses are shown as generic types, not specific models. Visual acuity is a rough model estimate.',
  ],

  'scene.chart': 'Eye chart',
  'scene.home': 'Home (reading)',
  'scene.city_day': 'City — day',
  'scene.city_night': 'City — night',
  'scene.night_lights': 'Night lights (close-up)',

  'home.tv.title': 'WEATHER',
  'home.tv.cities': ['Tashkent +31°', 'Samarkand +29°', 'Bukhara +33°', 'Nukus +27°'],
  'home.tv.ticker': 'Tomorrow: sunny, light wind',
  'home.laptop.title': 'How to protect your vision after 40',
  'home.laptop.body': [
    'Have your eyes checked yearly, even if you see well.',
    'Cataract develops slowly and often unnoticed.',
    'Early glaucoma causes no pain or discomfort.',
    'Take breaks when working at a screen.',
    'Wear sunglasses with a UV filter.',
    'If vision suddenly worsens, see a doctor at once.',
  ],
  'home.phone.msgs': [
    'Hello! A reminder about your eye doctor appointment',
    'Tomorrow, 10:00, room 204',
    "Thanks, I'll be there!",
    'Please bring your glasses',
  ],
  'home.calendar': 'September 2026',
  'city.phone.turn': 'turn right',
  'city.phone.eta': 'Arrive 18:56 · 4 km',
  'chart.near': [
    'Good vision',
    'helps you read',
    'books and messages',
    'without extra strain.',
    'Small print on medicine boxes',
    'and contracts needs sharp near vision.',
    'After 45 many people need reading glasses',
    'or lenses that focus at several distances.',
  ],
};
